import _ from 'lodash';

import {
  updateComment,
  replyToComment,
  deleteReplyToComment,
  updateReplyToComment,
} from '../../utils/api';
import logger from '../../utils/biLogger';
import {
  getFeedbackAddCommentOrReply,
  getFeedbackClickEditReply,
} from '@wix/bi-logger-get-feedback/v2';

function needsToUpdateServer(
  comment,
  { commentLocation: { x, y, pageId } = {}, message, status },
) {
  if (
    _.isMatch(comment.commentLocation, { x, y, pageId }) &&
    _.isMatch(comment.status, status) &&
    _.isMatch(comment.message, message)
  ) {
    return false;
  }
  return x || y || message || pageId || status;
}

export default class SiteRendererProps {
  constructor(dispatch, setShowError, editorType, setUser) {
    this.dispatch = dispatch;
    this.setShowError = setShowError;
    this.editorType = editorType;
    this.setUser = setUser;
  }

  closeAllComments() {
    this.setShowError(false);
    this.dispatch({ type: 'DE_ACTIVATE_COMMENTS' });
  }

  async onUpdateComment(comment, changes) {
    this.dispatch({ type: 'UPDATE_COMMENT', id: comment.id, changes });
    if (needsToUpdateServer(comment, changes)) {
      const changeKeys = Object.keys(changes);
      const fields = _.filter(changeKeys, function (o) {
        return o !== 'isActive' && o !== 'keepPopupOpen';
      });
      try {
        const { revision } = await updateComment({
          ...comment,
          ...changes,
          fields,
        });
        this.dispatch({
          type: 'UPDATE_COMMENT',
          id: comment.id,
          changes: { revision },
        });
      } catch (e) {
        this.setShowError(true);
      }
    }
  }

  async onAppendOwnerReply(comment, changes) {
    this.dispatch({ type: 'UPDATE_COMMENT', id: comment.id, changes });
    try {
      const serverComment = await replyToComment({ ...comment, ...changes });
      const {
        replies,
        revision,
        commentLocation: { pageId, breakpoint },
      } = serverComment;

      logger.report(
        getFeedbackAddCommentOrReply({
          origin: 'client-side-page',
          user_type: 'owner',
          type: 'reply',
          response_id: _.last(replies).id,
          response_number: _.size(replies) - 1,
          text: changes.replyText,
          page_id: pageId,
          comment_id: comment.id,
          editorVersion: this.editorType,
          breakpoint: JSON.stringify(breakpoint),
        }),
      );

      this.dispatch({
        type: 'UPDATE_COMMENT',
        id: comment.id,
        changes: { replies, revision },
      });
    } catch (e) {
      this.setShowError(true);
    }
  }

  async onDeleteReplyToComment(comment, reply) {
    const { feedbackId, revision } = comment;
    this.dispatch({
      type: 'DELETE_REPLY_TO_COMMENT',
      commentId: comment.id,
      replyId: reply.id,
    });
    try {
      const commentWithDeletedReply = await deleteReplyToComment({
        commentId: comment.id,
        feedbackId,
        commenterId: comment.commenterId,
        revision,
        replyId: reply.id,
      });
      this.dispatch({
        type: 'UPDATE_COMMENT',
        id: comment.id,
        changes: {
          revision: commentWithDeletedReply.revision,
          replies: commentWithDeletedReply.replies,
        },
      });
    } catch (e) {
      this.setShowError(true);
    }
  }

  async onUpdateReplyToComment(comment, reply, changes, user) {
    const { feedbackId, revision } = comment;
    const { message } = changes;
    this.dispatch({
      type: 'UPDATE_REPLY_TO_COMMENT',
      commentId: comment.id,
      replyId: reply.id,
      changes,
    });
    try {
      const updatedReplyComment = await updateReplyToComment({
        commentId: comment.id,
        feedbackId,
        revision,
        commenterId: comment.commenterId,
        replyId: reply.id,
        message,
      });

      logger.report(
        getFeedbackClickEditReply({
          origin: 'view-comment-page',
          comment_id: comment.id,
          editorVersion: this.editorType,
          breakpoint: JSON.stringify(
            updatedReplyComment.commentLocation.breakpoint,
          ),
          commentStatus: comment.status,
          ...(user.contactId && { contactId: user.contactId }),
        }),
      );

      this.dispatch({
        type: 'UPDATE_COMMENT',
        id: comment.id,
        changes: {
          revision: updatedReplyComment.revision,
        },
      });
    } catch (e) {
      this.setShowError(true);
    }
  }
}
